import React, { useRef, useState, useEffect } from "react";
import { Camera, Image as ImageIcon, Loader2 } from "lucide-react";

/**
 * Reusable "add photo" control that lets the user choose between the device
 * CAMERA (capture) or the GALLERY (file picker). Works on desktop and mobile.
 *
 * Props:
 *  - onFiles(FileList): called with the selected files
 *  - busy: shows a spinner + disables the trigger
 *  - label / testid / className: customise the trigger button
 *  - multiple: allow selecting several files (default true)
 *  - menuAlign: "right" | "left" — where the dropdown opens
 */
export function PhotoCaptureButton({
  onFiles,
  busy = false,
  label = "Add photo",
  testid = "add-photo",
  className = "",
  multiple = true,
  menuAlign = "right",
}) {
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const pick = (files) => {
    if (files && files.length) onFiles(files);
  };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={busy}
        data-testid={`${testid}-btn`}
        className={
          className ||
          "inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[13px] font-semibold disabled:opacity-60 transition-colors"
        }
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />} {label}
      </button>

      {open && (
        <div
          className={`absolute ${menuAlign === "left" ? "left-0" : "right-0"} mt-1.5 z-[60] w-52 rounded-xl border border-slate-200 bg-white shadow-xl overflow-hidden`}
          data-testid={`${testid}-menu`}
        >
          <button
            type="button"
            data-testid={`${testid}-camera`}
            onClick={() => { setOpen(false); cameraRef.current?.click(); }}
            className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-[13px] text-slate-700 hover:bg-slate-50"
          >
            <Camera className="h-4 w-4 text-blue-600" /> Take photo
          </button>
          <button
            type="button"
            data-testid={`${testid}-gallery`}
            onClick={() => { setOpen(false); galleryRef.current?.click(); }}
            className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-[13px] text-slate-700 hover:bg-slate-50 border-t border-slate-100"
          >
            <ImageIcon className="h-4 w-4 text-violet-600" /> Choose from gallery
          </button>
        </div>
      )}

      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple={multiple}
        hidden
        data-testid={`${testid}-camera-input`}
        onChange={(e) => { pick(e.target.files); e.target.value = ""; }}
      />
      <input
        ref={galleryRef}
        type="file"
        accept="image/*"
        multiple={multiple}
        hidden
        data-testid={`${testid}-gallery-input`}
        onChange={(e) => { pick(e.target.files); e.target.value = ""; }}
      />
    </div>
  );
}

export default PhotoCaptureButton;
